import React from "react";
import PropTypes from "prop-types";
import UserCard from "../user/UserCard";
import SkeletonLoader from "../common/SkeletonLoader";
import SearchEmptyState from "./SearchEmptyState";

/**
 * Component to display user search results
 *
 * Features:
 * - Displays list of users using UserCard component
 * - Loading states with skeleton loaders
 * - Empty state when no results found
 * - Compact mode for dropdown display
 */
const UserSearchResults = ({
  users = [],
  isLoading = false,
  isError = false,
  error = null,
  query = "",
  compact = false,
  onUserClick,
  maxResults,
  className = "",
}) => {
  // Loading state
  if (isLoading) {
    const skeletonCount = compact ? 3 : 5;
    return (
      <div className={`space-y-3 ${className}`}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-card shadow-card p-4 animate-pulse"
          >
            <div className="flex items-center gap-3">
              <SkeletonLoader variant="avatar" width="3rem" height="3rem" />
              <div className="flex-1 space-y-2">
                <SkeletonLoader variant="text" width="60%" height="1rem" />
                <SkeletonLoader variant="text" width="40%" height="0.875rem" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-deep-red font-medium mb-2">
          Error loading search results
        </div>
        <div className="text-medium-grey text-sm">
          {error?.message || "Something went wrong. Please try again."}
        </div>
      </div>
    );
  }

  // Don't show results if query is empty or too short
  if (!query || query.trim().length < 2) {
    return (
      <div className={className}>
        <SearchEmptyState query={query} compact={compact} />
      </div>
    );
  }

  // Empty state - no results
  if (!users || users.length === 0) {
    return (
      <div className={className}>
        <SearchEmptyState query={query} compact={compact} />
      </div>
    );
  }

  // Display results
  const displayUsers = maxResults ? users.slice(0, maxResults) : users;
  const hasMore = maxResults && users.length > maxResults;

  return (
    <div className={className}>
      <div className={compact ? "space-y-2" : "space-y-3"}>
        {displayUsers.map((user) => (
          <UserCard
            key={user._id || user.id}
            user={user}
            compact={compact}
            showFollowButton={true}
            onClick={onUserClick}
            className="hover:shadow-lg transition-shadow duration-200"
          />
        ))}
      </div>

      {/* Show "more results" indicator */}
      {hasMore && (
        <div className="text-center mt-4 text-sm text-medium-grey">
          +{users.length - maxResults} more results
        </div>
      )}
    </div>
  );
};

UserSearchResults.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.string,
      username: PropTypes.string,
      name: PropTypes.string,
      picture: PropTypes.string,
      verified: PropTypes.bool,
    }),
  ),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  error: PropTypes.object,
  query: PropTypes.string,
  compact: PropTypes.bool,
  onUserClick: PropTypes.func,
  maxResults: PropTypes.number,
  className: PropTypes.string,
};

export default UserSearchResults;
